import { useEffect, useState } from 'react';
import { CircleCheck, Megaphone, ScanLine, Smartphone, TriangleAlert, UserCheck, X } from 'lucide-react';
import {
  getApiError,
  safepassApi,
  useCancelCallMutation,
  useGetDismissalBoardQuery,
  type DismissalRow,
} from '@/store/api';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { getSocket, useSocketStatus } from '@/lib/socket';
import { Avatar } from '@/components/ui/Avatar';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageLayout } from '@/components/ui/PageLayout';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const timeFmt = new Intl.DateTimeFormat('en-GH', { hour: '2-digit', minute: '2-digit' });

/*
  ═════════════════════════════════════════════════════════════════════════════
  THE DISMISSAL BOARD

  This screen is meant to be left open on a classroom tablet or a screen in the
  staff room, and read from across a room by somebody who is not looking for
  anything in particular. Everything below follows from that.

  Grouped by class, because the question it answers is a teacher's
  ─────────────────────────────────────────────────────────────────
  A gate officer asks "who is next". A teacher asks "is anyone in MY class
  wanted". One flat list ordered by time makes the second question a search
  through every other class; grouping makes it a glance.

  Sorted oldest-first inside each class
  ─────────────────────────────────────
  The child who has been standing longest is the one somebody should move now.

  The waiting time is the headline number
  ───────────────────────────────────────
  Not the time they were called — a clock time forces the reader to subtract.
  "6m" is the thing staff act on, and it turns amber past five minutes because
  that is roughly when a parent starts wondering whether anyone heard them.

  Live, not polled
  ────────────────
  Every call and release broadcasts `dismissal_changed` over the socket that
  already exists; this invalidates on that. A board that lagged a minute behind
  would have teachers walking children down for parents who had already left.
  The connection light in the command bar is what says whether to trust it.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function DismissalBoardPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');
  const dispatch = useDispatch();
  const live = useSocketStatus();

  const { data, isLoading, isError, error } = useGetDismissalBoardQuery(institutionId, {
    skip: !institutionId,
    /*
      A slow poll behind the socket, not instead of it. The waiting minutes have
      to keep counting up even when nothing has changed, and it is the net that
      catches a missed broadcast on a flaky school connection.
    */
    pollingInterval: 30_000,
  });

  useEffect(() => {
    const socket = getSocket();
    const onChange = () => dispatch(safepassApi.util.invalidateTags(['Dismissal']));
    socket.on('dismissal_changed', onChange);
    return () => {
      socket.off('dismissal_changed', onChange);
    };
  }, [dispatch]);

  const waiting = data?.waiting ?? [];
  /* A teacher's own classes first. The question they came to answer is "is
     anyone of mine waiting", and scrolling past four other classes to find out
     is the thing this screen exists to avoid. */
  const ordered = [...waiting].sort((a, b) => Number(b.isMine) - Number(a.isMine));
  const byClass = ordered.reduce<Record<string, DismissalRow[]>>((acc, row) => {
    const key = row.studentClass || 'No class';
    (acc[key] ??= []).push(row);
    return acc;
  }, {});

  return (
    <PageLayout
      title="Dismissal board"
      subtitle="Children called to the gate, waiting to be collected"
      breadcrumbs={[{ label: 'Gate' }, { label: 'Dismissal board' }]}
      contained={false}
      meta={
        <>
          <Mark tone={waiting.length > 0 ? 'warning' : 'neutral'}>
            {waiting.length} waiting
          </Mark>
          <Mark tone="success">{data?.released.length ?? 0} collected today</Mark>
          {!live && <Mark tone="danger">Live feed down — this may be stale</Mark>}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {isLoading ? (
          <div className={cn(styles.panel, 'flex justify-center py-12')}>
            <Spinner />
          </div>
        ) : isError ? (
          <div className={cn(styles.panel, 'overflow-clip')}>
            <EmptyState icon={<TriangleAlert size={17} />} title="Could not load the board" description={getApiError(error)} />
          </div>
        ) : waiting.length === 0 ? (
          <div className={cn(styles.panel, 'overflow-clip')}>
            <EmptyState
              icon={<CircleCheck size={17} />}
              title="Nobody is waiting"
              description="Children appear here the moment a parent announces they have arrived, or an officer calls them from the gate."
            />
          </div>
        ) : (
          Object.entries(byClass).map(([className, rows]) => (
            <div key={className} className={cn(styles.panel, 'overflow-clip', rows[0]?.isMine && 'border-info/40')}>
              <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <h2 className={styles.sectionTitle}>{className}</h2>
                  {rows[0]?.isMine && <Mark tone="info">Your class</Mark>}
                </div>
                <Mark tone="warning">{rows.length}</Mark>
              </div>
              {rows.map((row) => (
                <WaitingRow key={row._id} row={row} institutionId={institutionId} />
              ))}
            </div>
          ))
        )}

        {(data?.released.length ?? 0) > 0 && (
          <div className={cn(styles.panel, 'overflow-clip')}>
            <div className="border-b border-border px-4 py-2.5">
              <h2 className={styles.sectionTitle}>Collected today</h2>
            </div>
            {data!.released.slice(0, 12).map((row) => (
              <div key={row._id} className="flex items-center gap-3 border-b border-border px-4 py-2 last:border-b-0">
                <UserCheck size={14} className="flex-shrink-0 text-[hsl(var(--record-ink-success))]" />
                <span className="min-w-0 flex-1 truncate text-[12px] text-foreground">{row.studentName}</span>
                <span className={cn(styles.meta, 'flex-shrink-0 tabular-nums')}>
                  {row.releasedAt ? timeFmt.format(new Date(row.releasedAt)) : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
}

/*
  ─── What the two controls on a row mean ────────────────────────────────────

  They are not two flavours of the same thing, and the screen used to let them
  look like it: the only control was a bare ✕, one tap, no confirmation, which
  takes the child off the queue and RECORDS NOTHING. An officer could clear a row
  believing they had just released somebody, and the audit log would have no
  idea the child had gone.

  So:

  · Release  — goes to the gate screen. It does not release anybody from here.
               There is no "release" button on this board on purpose: a release
               needs the code presented in person by the adult collecting, with
               an officer looking at both of them, and a board left open on a
               classroom tablet cannot possibly do that. This is a shortcut to
               the place where it can.

  · ✕        — takes the child off the queue and asks first, saying in as many
               words that it is not a release. A parent who left and came back
               is re-queued on the same row, so nothing is lost by using it.
*/
function WaitingRow({ row, institutionId }: { row: DismissalRow; institutionId: string }) {
  const [cancelCall, { isLoading }] = useCancelCallMutation();
  const [confirming, setConfirming] = useState(false);
  const navigate = useNavigate();
  const role = useAuthStore((st) => st.user?.role);
  /* The roles the gate endpoints already authorise. A teacher reading this on a
     classroom tablet is not one of them. */
  const canWorkTheGate =
    role === 'gate_officer' || role === 'institution_admin' || role === 'super_admin';

  /* Five minutes is about when a parent starts wondering whether anyone heard
     them, so that is where this stops being neutral. */
  const late = row.waitingMinutes >= 5;

  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0">
      <Avatar src={row.photoUrl} name={row.studentName} size={44} zoomable caption={row.studentClass} />

      <div className="min-w-0 flex-1">
        <p className="truncate text-[13.5px] font-bold text-foreground">{row.studentName}</p>
        <p className={cn(styles.meta, 'flex items-center gap-1.5')}>
          {row.calledVia === 'parent_app' ? (
            <>
              <Smartphone size={10} /> Parent is here
            </>
          ) : (
            <>
              <Megaphone size={10} /> Called at the gate
            </>
          )}
          <span className="opacity-70">· {timeFmt.format(new Date(row.calledAt))}</span>
        </p>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <span
          className={cn(
            styles.figure,
            'text-[15px] tabular-nums',
            late ? 'text-[hsl(var(--record-ink-warning))]' : 'text-muted-foreground',
          )}
        >
          {row.waitingMinutes}m
        </span>
        {canWorkTheGate && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/gate')}
            title="Opens the gate screen — the code is still taken in person"
          >
            <ScanLine size={13} className="mr-1" />
            Release
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={`Take ${row.studentName} off the queue — this does not record a release`}
          loading={isLoading}
          onClick={() => setConfirming(true)}
        >
          <X size={14} />
        </Button>
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          void cancelCall({ institutionId, entryId: row._id });
          setConfirming(false);
        }}
        title={`Take ${row.studentName} off the queue?`}
        confirmLabel="Take off the queue"
        loading={isLoading}
        body={
          <>
            This only clears the row. It does{' '}
            <strong className="text-foreground">not</strong> record that{' '}
            {row.studentName} was collected, and nothing goes into the audit log.
            <br />
            <br />
            To release them, take their code at the gate. If the parent comes back later they can
            be called again.
          </>
        }
      />
    </div>
  );
}
